import { ArrowRight, MinusCircle, PlusCircle } from 'lucide-react';
import { computeDiff, formatDiffValue, type DiffLine } from './computeDiff';
import { cn } from '@/lib/utils';

interface DiffViewerProps {
  before: unknown;
  after: unknown;
  className?: string;
  emptyLabel?: string;
}

export function DiffViewer({ before, after, className, emptyLabel = 'No changes' }: DiffViewerProps) {
  const lines = computeDiff(before, after);
  if (lines.length === 0) {
    return (
      <div className={cn('rounded-md border border-dashed bg-muted/30 p-3 text-xs text-muted-foreground', className)}>
        {emptyLabel}
      </div>
    );
  }
  return (
    <ul className={cn('divide-y rounded-md border bg-muted/20 text-xs', className)}>
      {lines.map((line, i) => (
        <li key={`${line.path}-${i}`} className="flex items-start gap-2 px-3 py-2 font-mono">
          <DiffIcon line={line} />
          <span className="truncate font-medium text-foreground">{line.path}</span>
          <span className="ml-auto flex min-w-0 items-center gap-2 text-right">
            {line.kind !== 'add' && (
              <span className="truncate text-muted-foreground line-through">
                {formatDiffValue((line as { before?: unknown }).before)}
              </span>
            )}
            {line.kind === 'change' && <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />}
            {line.kind !== 'remove' && (
              <span className="truncate text-foreground">
                {formatDiffValue((line as { after?: unknown }).after)}
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

function DiffIcon({ line }: { line: DiffLine }) {
  if (line.kind === 'add') return <PlusCircle className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--success))]" aria-hidden />;
  if (line.kind === 'remove') return <MinusCircle className="h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden />;
  return <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />;
}
